import styled from 'styled-components';
import {
  BodyBoldS,
  BodyRegularS,
  CaptionRegularL,
  HeadingExtraBoldM,
  HeadingExtraBoldS,
  LabelRegularBoldM,
} from '../../styles/globals';
import { IThemeProvider } from '../../styles/theme';

const GUIDE_CARD_SHADOW = '0 1px 2px rgba(45, 45, 45, 0.05)';

export const HelpPrompt = styled.div`
  margin: 24px 24px 20px;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ theme }: IThemeProvider) => `
    border: 1px solid ${theme.colors.primary[30]};
    background: ${theme.colors.primary[10]};
  `}
`;

export const HelpPromptText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[90]};
  `}
`;

export const HelpPromptButton = styled.button`
  align-self: flex-start;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  font: inherit;
  font-weight: 500;
  cursor: pointer;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[0]};
    background: ${theme.colors.primary[100]};

    &:hover {
      background: ${theme.colors.primary[70]};
    }
  `}
`;

export const GuideBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(69, 85, 95, 0.62);
`;

export const GuideModal = styled.div`
  position: fixed;
  z-index: 3100;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(1080px, calc(100vw - 16px));
  max-height: calc(100vh - 16px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;

  font-family: Roboto, sans-serif;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[90]};
    background: ${theme.colors.neutral.primary[0]};
  `}
`;

export const GuideHeader = styled.header`
  position: relative;
  flex: 0 0 auto;
  padding: 42px 38px 32px;

  ${({ theme }: IThemeProvider) => `
    border-bottom: 1px solid ${theme.colors.neutral.primary[10]};
  `}
`;

export const GuideKickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 32px;
`;

export const GuideKickerLabel = styled(CaptionRegularL).attrs({ as: 'span' })`
  letter-spacing: 0.01em;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[60]};
  `}
`;

export const GuideSurveyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
    border: 1px solid ${theme.colors.primary[30]};
  `}
`;

export const EffectSectionTitle = styled(HeadingExtraBoldS).attrs({ as: 'h3' })`
  margin: 0 0 18px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const ParticipantsSectionTitle = styled(EffectSectionTitle)`
  font-weight: 900;
`;

export const GuideCloseButton = styled.button`
  position: absolute;
  top: 32px;
  right: 36px;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  ${({ theme }: IThemeProvider) => `
    background: ${theme.colors.primary[10]};

    &:hover {
      background: ${theme.colors.primary[30]};
    }
  `}
`;

export const GuideTitle = styled(HeadingExtraBoldM).attrs({ as: 'h2' })`
  margin: 0 0 12px;
  font-family: Roboto, sans-serif;
  font-weight: 600;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const GuideSubtitle = styled(BodyRegularS).attrs({ as: 'p' })`
  margin: 0;
  line-height: 24px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[70]};
  `}
`;

export const GuideBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-color: ${({ theme }: IThemeProvider) =>
    `${theme.colors.neutral.primary[50]} transparent`};
  scrollbar-width: thin;
`;

export const GuideSection = styled.section`
  margin: 0 38px;
  padding: 34px 0;

  ${({ theme }: IThemeProvider) => `
    border-bottom: 1px solid ${theme.colors.neutral.primary[10]};
  `}
`;

export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
`;

export const SectionNumber = styled(LabelRegularBoldM).attrs({ as: 'span' })`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  border-radius: 50%;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
  `}
`;

export const SectionTitle = styled(HeadingExtraBoldS).attrs({ as: 'h3' })`
  margin: 0;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const SectionDescriptionEmphasis = styled.strong`
  font-weight: 700;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const SectionDescription = styled(BodyRegularS).attrs({ as: 'p' })`
  margin: 0 0 24px;
  line-height: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  strong,
  ${SectionDescriptionEmphasis} {
    font-weight: 700;
  }

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[70]};

    strong,
    ${SectionDescriptionEmphasis} {
      color: ${theme.colors.neutral.primary[100]};
    }
  `}
`;

export const VisualLabel = styled(CaptionRegularL).attrs({ as: 'p' })`
  margin: 0 0 10px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[50]};
  `}
`;

type ActiveProps = {
  $active: boolean;
  $direct?: boolean;
  $contextActive?: boolean;
  $parentDirect?: boolean;
  $standalone?: boolean;
};

export const HierarchyCanvas = styled.div`
  min-height: 286px;
  margin-bottom: 20px;
  padding: 26px;
  display: grid;
  grid-template-columns: 1.7fr 0.95fr;
  grid-template-rows: auto auto;
  gap: 18px;
  align-items: start;
  border-radius: 14px;

  ${({ theme }: IThemeProvider) => `
    background: ${theme.colors.primary[10]};
  `}

  > button {
    grid-column: 1;
    justify-self: start;
  }
`;

export const HierarchyTopGroup = styled.div<ActiveProps>`
  min-height: 170px;
  padding: 18px;
  border: 1px solid;
  border-radius: 16px;
  box-sizing: border-box;
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease;

  &:first-of-type {
    display: grid;
    grid-template-columns: minmax(205px, 1fr) auto;
    grid-template-rows: auto 1fr;
    gap: 10px;

    > strong {
      grid-column: 1 / -1;
    }
  }

  &:nth-of-type(2) {
    > div {
      display: flex;
      gap: 10px;
      margin-top: 12px;
    }
  }

  ${({ theme, $active }: IThemeProvider & ActiveProps) => `
    color: ${$active ? theme.colors.neutral.primary[0] : theme.colors.primary[100]};
    border-color: ${theme.colors.primary[100]};
    background: ${$active ? theme.colors.primary[100] : 'transparent'};
  `}
`;

export const HierarchyTopLabel = styled(BodyBoldS).attrs({ as: 'strong' })``;

export const HierarchyChildGroup = styled.div<ActiveProps>`
  min-height: 96px;
  padding: 14px 16px;
  border: 1px solid;
  border-radius: 12px;
  box-sizing: border-box;
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease;

  > span {
    display: block;
    margin-bottom: 12px;
    font-size: 16px;
    line-height: 19px;
    font-weight: 500;
  }

  > div {
    display: flex;
    gap: 10px;
  }

  ${({ theme, $active, $parentDirect }: IThemeProvider & ActiveProps) => {
    const highlighted = $active || $parentDirect;
    const color = highlighted
      ? theme.colors.neutral.primary[0]
      : theme.colors.primary[100];
    const borderColor = highlighted
      ? theme.colors.primary[70]
      : theme.colors.neutral.primary[20];
    const background = highlighted
      ? 'rgba(24, 58, 96, 0.12)'
      : theme.colors.neutral.primary[0];

    return `
      color: ${color};
      border-color: ${borderColor};
      background: ${background};
    `;
  }}
`;

export const HierarchyLeaf = styled.button<ActiveProps>`
  min-width: 106px;
  height: 44px;
  padding: 0 18px;
  border: 1px solid;
  border-radius: 12px;
  font-family: inherit;
  font-size: 16px;
  line-height: 19px;
  font-weight: 400;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease,
    box-shadow 150ms ease;

  ${({
    theme,
    $active,
    $contextActive,
    $parentDirect,
    $standalone,
  }: IThemeProvider & ActiveProps) => {
    const transparent = $parentDirect && !$standalone;
    const activeOrTransparent = $active || transparent;
    const color = activeOrTransparent
      ? theme.colors.neutral.primary[0]
      : theme.colors.primary[100];
    const borderColor = $active
      ? theme.colors.primary[70]
      : $standalone
        ? theme.colors.primary[100]
        : theme.colors.neutral.primary[20];
    const background = $active
      ? theme.colors.primary[100]
      : transparent
        ? 'rgba(255, 255, 255, 0.06)'
        : theme.colors.neutral.primary[0];
    const contextColor =
      $contextActive && !$active && !transparent
        ? `color: ${theme.colors.primary[100]};`
        : '';

    return `
      color: ${color};
      border-color: ${borderColor};
      background: ${background};
      box-shadow: ${activeOrTransparent ? 'none' : GUIDE_CARD_SHADOW};
      ${contextColor}
    `;
  }}
`;

export const ExampleTable = styled.table`
  width: 100%;
  margin-bottom: 0;
  table-layout: fixed;
  border-spacing: 0;
  border-collapse: separate;
  overflow: hidden;
  border-radius: 14px;

  ${({ theme }: IThemeProvider) => `
    border: 1px solid ${theme.colors.neutral.primary[10]};
  `}
`;

export const EffectExampleTable = styled(ExampleTable)`
  max-width: 360px;
  box-shadow: ${GUIDE_CARD_SHADOW};
`;

export const ExampleTableRow = styled.tr<{ $active?: boolean }>`
  outline: none;
  transition: color 150ms ease, background 150ms ease;

  ${({ theme, $active }: IThemeProvider & { $active?: boolean }) => `
    color: ${$active ? theme.colors.neutral.primary[0] : theme.colors.neutral.primary[90]};
    background: ${$active ? theme.colors.primary[100] : theme.colors.neutral.primary[0]};

    &:focus-visible {
      box-shadow: inset 0 0 0 2px ${theme.colors.primary[70]};
    }
  `}
`;

export const ExampleTableHead = styled(LabelRegularBoldM).attrs({ as: 'th' })`
  padding: 12px 18px;
  text-align: left;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[50]};
    background: ${theme.colors.neutral.primary[5]};
    border-right: 1px solid ${theme.colors.neutral.primary[10]};

    &:last-child {
      border-right: none;
    }
  `}
`;

export const ExampleTableCell = styled(BodyRegularS).attrs({ as: 'td' })`
  height: 46px;
  padding: 0 18px;
  box-sizing: border-box;

  ${({ theme }: IThemeProvider) => `
    border-top: 1px solid ${theme.colors.neutral.primary[10]};
    border-right: 1px solid ${theme.colors.neutral.primary[10]};

    &:last-child {
      border-right: none;
    }

    span {
      color: ${theme.colors.neutral.primary[20]};
    }
  `}
`;

export const MiniDiagram = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1.5fr;
  gap: 20px;
  align-items: center;
  margin-top: 28px;
`;

export const MiniDiagramColumn = styled.div`
  min-width: 0;
`;

export const MiniDiagramLabel = styled(CaptionRegularL).attrs({ as: 'p' })`
  margin: 0 0 10px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[50]};
  `}
`;

const MiniGroup = styled.div`
  min-height: 108px;
  padding: 18px;
  border-radius: 12px;
  box-sizing: border-box;

  strong {
    display: block;
    margin-bottom: 12px;
    font-size: 16px;
    line-height: 19px;
    font-weight: 700;
    color: ${({ theme }: IThemeProvider) => theme.colors.primary[100]};
  }

  > div {
    display: flex;
    gap: 10px;
  }

  ${({ theme }: IThemeProvider) => `
    border: 1px solid ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
  `}
`;

export const MiniBefore = styled(MiniGroup)``;
export const MiniAfter = styled(MiniGroup)``;

export const MiniNode = styled(CaptionRegularL).attrs({ as: 'span' })`
  min-width: 90px;
  padding: 10px 12px;
  border-radius: 9px;
  text-align: center;
  box-sizing: border-box;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    border: 1px solid ${theme.colors.neutral.primary[20]};
    background: ${theme.colors.neutral.primary[0]};
    box-shadow: ${GUIDE_CARD_SHADOW};
  `}
`;

export const MiniArrow = styled.span`
  padding-top: 28px;
  font-size: 22px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[30]};
  `}
`;

export const MoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 26px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 16px;
  line-height: 19px;
  font-weight: 400;
  cursor: pointer;

  span {
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    border: 1px solid currentColor;
    border-radius: 50%;
    line-height: 1;
  }

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[50]};

    &:hover {
      color: ${theme.colors.primary[100]};
    }
  `}
`;

export const MorePanel = styled.div`
  margin-top: 28px;
`;

export const MoreSubsection = styled.div`
  &:not(:first-of-type) {
    margin-top: 28px;
  }
`;

export const MoreSubsectionTitle = styled(BodyBoldS).attrs({ as: 'h4' })`
  margin: 0 0 10px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const MoreSubsectionText = styled(BodyRegularS).attrs({ as: 'p' })`
  margin: 0;
  line-height: 24px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[70]};
  `}
`;

export const MoreInlineCode = styled(CaptionRegularL).attrs({ as: 'code' })`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 4px;
  vertical-align: baseline;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
    border: 1px solid ${theme.colors.primary[30]};
  `}
`;

export const MoreCharacterBlock = styled(CaptionRegularL).attrs({ as: 'pre' })`
  margin: 12px 0 0;
  padding: 14px 16px;
  border-radius: 8px;
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
    background: ${theme.colors.neutral.primary[5]};
  `}
`;

export const MoreCallout = styled.div`
  margin-top: 28px;
  padding: 18px 20px;
  border-radius: 8px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
    border: 1px solid ${theme.colors.primary[30]};
  `}
`;

export const MoreCalloutTitle = styled(BodyBoldS).attrs({ as: 'p' })`
  margin: 0 0 8px;
  font-weight: 800;
`;

export const MoreCalloutText = styled(BodyRegularS).attrs({ as: 'p' })`
  margin: 0;
  line-height: 24px;
`;

export const GuideFooter = styled.footer`
  flex: 0 0 auto;
  min-height: 88px;
  padding: 18px 38px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;

  ${({ theme }: IThemeProvider) => `
    border-top: 1px solid ${theme.colors.neutral.primary[10]};
    background: ${theme.colors.neutral.primary[0]};
  `}
`;

export const GuidePrimaryButton = styled.button`
  min-width: 86px;
  height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 19px;
  font-weight: 700;
  cursor: pointer;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[0]};
    background: ${theme.colors.primary[100]};

    &:hover {
      background: ${theme.colors.primary[70]};
    }
  `}
`;

export const ParticipantsGuideSection = styled(GuideSection)`
  border-bottom: none;
`;

export const ColumnReferenceTable = styled.table`
  width: 100%;
  margin: 0 0 28px;
  border-spacing: 0;
  border-collapse: separate;
  overflow: hidden;
  border-radius: 14px;
  table-layout: fixed;

  ${({ theme }: IThemeProvider) => `
    border: 1px solid ${theme.colors.neutral.primary[10]};
  `}
`;

export const ColumnReferenceHead = styled(LabelRegularBoldM).attrs({ as: 'th' })`
  padding: 12px 14px;
  text-align: left;
  vertical-align: top;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
    background: ${theme.colors.neutral.primary[5]};
    border-bottom: 1px solid ${theme.colors.neutral.primary[10]};
    border-right: 1px solid ${theme.colors.neutral.primary[10]};

    &:last-child {
      border-right: none;
    }
  `}
`;

export const ColumnReferenceCell = styled(CaptionRegularL).attrs({ as: 'td' })`
  padding: 12px 14px;
  vertical-align: top;
  line-height: 20px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[80]};
    border-bottom: 1px solid ${theme.colors.neutral.primary[10]};
    border-right: 1px solid ${theme.colors.neutral.primary[10]};

    &:last-child {
      border-right: none;
    }

    tr:last-child & {
      border-bottom: none;
    }
  `}
`;

export const ColumnReferenceColumnCell = styled(ColumnReferenceCell)`
  font-weight: 700;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const RequiredYes = styled.span`
  font-family: Roboto, sans-serif;
  font-weight: 600;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
  `}
`;

export const ParticipantsExampleTable = styled(ExampleTable)`
  box-shadow: ${GUIDE_CARD_SHADOW};

  th:nth-child(1),
  td:nth-child(1) {
    width: 28%;
    white-space: nowrap;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 14%;
  }

  th:nth-child(3),
  td:nth-child(3),
  th:nth-child(4),
  td:nth-child(4) {
    width: 13%;
  }
`;

export const EffectParticipantsExampleTable = styled(ParticipantsExampleTable)`
  th:nth-child(1),
  td:nth-child(1) {
    width: 30%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 18%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 22%;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 20%;
  }

  th:nth-child(5),
  td:nth-child(5) {
    width: 10%;
  }
`;

export const ReportAccessColumnReferenceTable = styled(ColumnReferenceTable)`
  th:nth-child(1),
  td:nth-child(1) {
    width: 24%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 16%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 60%;
  }
`;

export const ReportAccessExampleTable = styled(ExampleTable)`
  box-shadow: ${GUIDE_CARD_SHADOW};

  th:nth-child(1),
  td:nth-child(1) {
    width: 24%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 30%;
    white-space: nowrap;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 26%;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 20%;
  }
`;

export const InfoCalloutList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const InfoCallout = styled.div`
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 10px;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.primary[100]};
    background: ${theme.colors.primary[10]};
    border: 1px solid ${theme.colors.primary[30]};
  `}
`;

export const InfoCalloutIcon = styled.div`
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 50%;

  svg {
    width: 14px;
    height: 14px;
  }

  ${({ theme }: IThemeProvider) => `
    background: ${theme.colors.primary[100]};

    svg path {
      fill: ${theme.colors.neutral.primary[0]};
    }
  `}
`;

export const InfoCalloutContent = styled.div`
  min-width: 0;
`;

export const InfoCalloutTitle = styled(BodyBoldS).attrs({ as: 'p' })`
  margin: 0 0 6px;
`;

export const InfoCalloutText = styled(BodyRegularS).attrs({ as: 'p' })`
  margin: 0;
  line-height: 24px;

  strong {
    font-weight: 700;
  }
`;

export const Template = styled.div`
  margin: 0 24px 24px;

  b {
    font-weight: 500;
  }

  button {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
    margin-left: 4px;

    ${({ theme }: IThemeProvider) => `
      color: ${theme.colors.primary[100]};
    `}
  }
`;

export const ResultBox = styled.div<{ mode: 'success' | 'error' }>`
  margin: 0 24px 24px;
  padding: 12px 16px;
  border-radius: 6px;

  ${({ theme, mode }: IThemeProvider & { mode: 'success' | 'error' }) => {
    const accent = mode === 'success' ? theme.colors.accent.green : theme.colors.accent.red;
    return `
      border: 1px solid ${accent[100]};
      background: ${accent[10]};
    `;
  }}
`;

export const ResultTitle = styled.p`
  margin: 0 0 4px;
  font-weight: 500;
`;

export const ResultSummary = styled.p`
  margin: 0;
  font-size: 14px;
`;

export const ErrorTable = styled.div`
  margin: 0 24px 24px;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 6px;

  ${({ theme }: IThemeProvider) => `
    border: 1px solid ${theme.colors.neutral.primary[30]};
  `}
`;

export const ErrorRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  font-size: 14px;

  ${({ theme }: IThemeProvider) => `
    border-bottom: 1px solid ${theme.colors.neutral.primary[20]};

    &:last-of-type {
      border-bottom: none;
    }
  `}
`;

export const ErrorRowNumber = styled.span`
  flex: 0 0 68px;
  font-weight: 500;

  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[70]};
  `}
`;

export const ErrorMessages = styled.span`
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
`;
